// SignatureUploadService.js
//
// Upload written signature PNGs to Firebase Storage.

import { deleteObject, ref, uploadBytes } from 'firebase/storage';

import { storage } from 'firebaseConfig';
import { assertBaseUploadAllowed } from 'services/bases/BaseUploadPolicyService';
import { prepareBaseUploadIntent } from 'services/storage/StorageUploadIntentService';

const OUTPUT_MIME_TYPE = 'image/png';

function normalizeStoragePath(value) {
  return String(value || '').trim().replace(/^\/+/, '');
}

function cleanString(value) {
  return String(value || '').trim();
}

/**
 * @param {string} dataUrl
 * @returns {Blob}
 */
export function dataUrlToBlob(dataUrl) {
  const raw = String(dataUrl || '');
  const match = raw.match(/^data:([^;,]+)?(;base64)?,(.*)$/);
  if (!match) {
    throw new Error('Invalid signature image data.');
  }

  const mimeType = cleanString(match[1]) || OUTPUT_MIME_TYPE;
  const isBase64 = Boolean(match[2]);
  const payload = match[3] || '';

  if (isBase64) {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: mimeType });
  }

  return new Blob([decodeURIComponent(payload)], { type: mimeType });
}

async function deleteStorageObjectIfPresent(storagePath) {
  const normalizedPath = normalizeStoragePath(storagePath);
  if (!normalizedPath) return;

  try {
    await deleteObject(ref(storage, normalizedPath));
  } catch (error) {
    if (error?.code === 'storage/object-not-found') return;
    console.warn('[SignatureUploadService] failed to delete previous signature', error);
  }
}

/**
 * Uploads a written signature PNG for a node field.
 *
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.nodeKey
 * @param {string} options.dataUrl
 * @param {string|null} [options.previousStoragePath]
 * @param {string|null} [options.fieldName]
 * @returns {Promise<string>} Storage path
 */
export async function uploadSignatureImage({
  baseKey,
  nodeKey,
  dataUrl,
  previousStoragePath = null,
  fieldName = null,
}) {
  const cleanedBaseKey = cleanString(baseKey);
  const cleanedNodeKey = cleanString(nodeKey);
  if (!cleanedBaseKey || !cleanedNodeKey) {
    throw new Error('Base and node are required to upload a signature.');
  }

  const blob = dataUrlToBlob(dataUrl);
  if (!blob.size) {
    throw new Error('Signature image is empty.');
  }

  const fieldStem = cleanString(fieldName)
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'signature';

  const storagePath = `nodes/${cleanedNodeKey}/signatures/${fieldStem}-${Date.now()}.png`;
  const expectedPrefix = `nodes/${cleanedNodeKey}/signatures/`;

  await assertBaseUploadAllowed({
    baseKey: cleanedBaseKey,
    fileName: `${fieldStem}.png`,
    fileSizeBytes: blob.size,
  });

  const uploadIntent = await prepareBaseUploadIntent({
    baseKey: cleanedBaseKey,
    storagePath,
    fileSizeBytes: blob.size,
    contentType: OUTPUT_MIME_TYPE,
    uploadKind: 'signature',
    nodeKey: cleanedNodeKey,
    itemKey: cleanedNodeKey,
  });
  const intentId = cleanString(uploadIntent?.intentId);

  await uploadBytes(ref(storage, storagePath), blob, {
    contentType: OUTPUT_MIME_TYPE,
    cacheControl: 'public, max-age=3600',
    customMetadata: {
      intentId,
      nodeKey: cleanedNodeKey,
      baseKey: cleanedBaseKey,
      fieldName: cleanString(fieldName) || '',
    },
  });

  const previousPath = normalizeStoragePath(previousStoragePath);
  if (previousPath.startsWith(expectedPrefix) && previousPath !== storagePath) {
    await deleteStorageObjectIfPresent(previousPath);
  }

  return storagePath;
}

/**
 * Best-effort delete of a prior signature image under the node signatures prefix.
 *
 * @param {string|null|undefined} storagePath
 * @param {string|null|undefined} nodeKey
 * @returns {Promise<void>}
 */
export async function deleteSignatureImageIfOwned(storagePath, nodeKey) {
  const cleanedNodeKey = cleanString(nodeKey);
  const normalizedPath = normalizeStoragePath(storagePath);
  if (!cleanedNodeKey || !normalizedPath) return;
  if (!normalizedPath.startsWith(`nodes/${cleanedNodeKey}/signatures/`)) return;
  await deleteStorageObjectIfPresent(normalizedPath);
}
